import { Badge, Button, PageHeader, Skeleton, Tabs, useToast } from '@zyxantech/tux'
import { RotateCcw, Save } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useDeleteSettingMutation, useGetSettingsQuery, useUpdateSettingMutation, type Setting } from '@/api/settings.api'
import { ErrorState } from '@/components/shared/ErrorState'
import { ACTION, RESOURCE } from '@/constants/permissions'
import { usePermission } from '@/hooks/usePermission'

/** Group settings by the first segment of their dot-separated key. */
function groupSettings(settings: Setting[]): Map<string, Setting[]> {
  const groups = new Map<string, Setting[]>()
  for (const s of settings) {
    const prefix = s.key.split('.')[0] ?? 'general'
    const label = prefix.charAt(0).toUpperCase() + prefix.slice(1)
    const existing = groups.get(label) ?? []
    existing.push(s)
    groups.set(label, existing)
  }
  return groups
}

function valueLabel(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  if (typeof value === 'boolean' || typeof value === 'number') return String(value)
  return typeof value === 'string' ? value : JSON.stringify(value)
}

function valueType(value: unknown): 'boolean' | 'number' | 'json' | 'text' {
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return 'number'
  if (typeof value === 'object') return 'json'
  return 'text'
}

export function SettingsPage() {
  const toast = useToast()
  const canUpdate = usePermission(RESOURCE.SETTINGS, ACTION.UPDATE)

  const { data: settings = [], isLoading, error, refetch } = useGetSettingsQuery()
  const [updateSetting] = useUpdateSettingMutation()
  const [deleteSetting] = useDeleteSettingMutation()

  // Local overrides: key → string (serialized pending value), dirty = changed from server
  const [pending, setPending] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<Record<string, boolean>>({})

  const groups = useMemo(() => groupSettings(settings), [settings])
  const groupLabels = useMemo(() => [...groups.keys()], [groups])
  const [activeTab, setActiveTab] = useState<string>(() => groupLabels[0] ?? '')

  // Update activeTab when groups first load
  useMemo(() => {
    if (activeTab === '' && groupLabels.length > 0) {
      setActiveTab(groupLabels[0] ?? '')
    }
  }, [groupLabels, activeTab])

  const isDirty = (key: string) => key in pending

  const handleChange = (key: string, raw: string) => {
    setPending((p) => ({ ...p, [key]: raw }))
  }

  const handleSave = async (setting: Setting) => {
    const rawValue = pending[setting.key]
    if (rawValue === undefined) return
    setSaving((s) => ({ ...s, [setting.key]: true }))
    try {
      let value: unknown = rawValue
      const t = valueType(setting.value)
      if (t === 'boolean') value = rawValue === 'true'
      else if (t === 'number') value = Number(rawValue)
      else if (t === 'json') value = JSON.parse(rawValue)
      await updateSetting({ key: setting.key, body: { value } }).unwrap()
      setPending((p) =>
        Object.fromEntries(Object.entries(p).filter(([k]) => k !== setting.key)),
      )
      toast.show(`${setting.key} saved`, 'success')
    } catch {
      toast.show(`Failed to save ${setting.key}`, 'error')
    } finally {
      setSaving((s) => ({ ...s, [setting.key]: false }))
    }
  }

  const handleReset = async (key: string) => {
    setSaving((s) => ({ ...s, [key]: true }))
    try {
      await deleteSetting(key).unwrap()
      setPending((p) => Object.fromEntries(Object.entries(p).filter(([k]) => k !== key)))
      toast.show(`${key} reset to default`, 'success')
    } catch {
      toast.show(`Failed to reset ${key}`, 'error')
    } finally {
      setSaving((s) => ({ ...s, [key]: false }))
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton height={28} width="20%" />
        <Skeleton height={300} />
      </div>
    )
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorState error={error} retry={() => void refetch()} />
      </div>
    )
  }

  const activeGroup = groups.get(activeTab) ?? []

  return (
    <div className="p-6">
      <PageHeader
        title="Settings"
        subtitle="Tenant configuration — tenant overrides take precedence over system defaults"
      />

      {groupLabels.length === 0 ? (
        <p className="text-sm text-text-muted">No settings available.</p>
      ) : (
        <>
          <div className="pb-4">
            <Tabs
              items={groupLabels.map((l) => ({ id: l, label: l }))}
              value={activeTab}
              onChange={setActiveTab}
            />
          </div>

          <div className="space-y-3">
            {activeGroup.map((s) => (
              <SettingRow
                key={s.key}
                setting={s}
                pendingValue={pending[s.key]}
                isSaving={saving[s.key] ?? false}
                isDirty={isDirty(s.key)}
                canUpdate={canUpdate}
                onChange={(v) => handleChange(s.key, v)}
                onSave={() => void handleSave(s)}
                onReset={() => void handleReset(s.key)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

interface SettingRowProps {
  setting: Setting
  pendingValue: string | undefined
  isSaving: boolean
  isDirty: boolean
  canUpdate: boolean
  onChange: (value: string) => void
  onSave: () => void
  onReset: () => void
}

function SettingRow({
  setting,
  pendingValue,
  isSaving,
  isDirty,
  canUpdate,
  onChange,
  onSave,
  onReset,
}: SettingRowProps) {
  const displayValue = pendingValue ?? valueLabel(setting.value)
  const type = valueType(setting.value)
  const isSystemDefault = setting.source === 'system'

  return (
    <div
      className={[
        'rounded-lg border bg-bg-surface p-4',
        isDirty ? 'border-accent/40' : 'border-border-subtle',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium text-text-primary">{setting.key}</span>
            {isSystemDefault && (
              <Badge variant="default">system default</Badge>
            )}
            {isDirty && <Badge variant="warning">unsaved</Badge>}
          </div>
        </div>
        {canUpdate && (
          <div className="flex shrink-0 items-center gap-2">
            {isDirty && (
              <Button
                variant="primary"
                size="sm"
                icon={<Save size={13} />}
                loading={isSaving}
                onClick={onSave}
              >
                Save
              </Button>
            )}
            {!isSystemDefault && (
              <Button
                variant="ghost"
                size="sm"
                icon={<RotateCcw size={13} />}
                title="Reset to system default"
                loading={isSaving}
                onClick={onReset}
              />
            )}
          </div>
        )}
      </div>

      <div className="mt-3">
        {type === 'boolean' ? (
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={(pendingValue ?? String(setting.value)) === 'true'}
              disabled={!canUpdate}
              className="accent-accent"
              onChange={(e) => onChange(String(e.target.checked))}
            />
            <span className="text-sm text-text-secondary">
              {(pendingValue ?? String(setting.value)) === 'true' ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        ) : type === 'json' ? (
          <textarea
            value={displayValue}
            disabled={!canUpdate}
            rows={4}
            className="w-full resize-none rounded-md border border-border-subtle bg-bg-base px-3 py-2 font-mono text-xs text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input
            type={type === 'number' ? 'number' : 'text'}
            value={displayValue}
            disabled={!canUpdate}
            className="w-full rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </div>
    </div>
  )
}
